// Shared progress bar — used by Budgets page rows, Dashboard BudgetAlerts,
// AND Dashboard GoalsSummary. Default palette (red over / amber warn /
// slate healthy) is tuned for budgets where "over" is bad. The `variant`
// prop swaps to a goal-friendly palette where "over" means "completed"
// (emerald) instead of "overspent" (red).
//
// Phase 48b: extracted from DashboardCharts/BudgetAlerts so the Budgets
// page can use the same visual language.
// Phase 48f: added `variant: 'budget' | 'goal'` so completed savings
// goals show in emerald (success), not red (overspent).

import React from 'react';
import { formatPercent } from '../lib/format.js';

export function budgetLevel(pct) {
  if (pct >= 1) return 'over';
  if (pct >= 0.8) return 'warn';
  return 'ok';
}

export function budgetBarColor(level, variant = 'budget') {
  // Background fill (the moving bar).
  if (level === 'over') {
    return variant === 'goal' ? 'bg-emerald-500' : 'bg-red-500';
  }
  if (level === 'warn') return 'bg-amber-500';
  // Healthy — subtle slate so the bar blends in rather than screaming
  // for attention next to over-budget red ones.
  return 'bg-slate-300 dark:bg-slate-600';
}

export function budgetTextColor(level, variant = 'budget') {
  if (level === 'over') {
    return variant === 'goal'
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-red-600 dark:text-red-400';
  }
  if (level === 'warn') return 'text-amber-600 dark:text-amber-400';
  return 'text-slate-500 dark:text-slate-400';
}

/**
 * Compact bar used inside list rows. `ariaLabel` should be a full
 * sentence ("餐飲 used 65%") so screen-reader users get the same
 * context sighted users get from the visual.
 *
 * variant: 'budget' (default, red over) | 'goal' (emerald over).
 */
export default function BudgetProgressBar({ pct, ariaLabel, height = 'h-2', variant = 'budget' }) {
  const level = budgetLevel(pct);
  const widthPct = Math.min(Math.max(pct * 100, 0), 100);
  return (
    <div
      className={`w-full ${height} bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden`}
      role="progressbar"
      aria-valuenow={Math.round(widthPct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
    >
      <div
        className={`${budgetBarColor(level, variant)} h-full transition-all`}
        style={{ width: `${widthPct}%` }}
      />
    </div>
  );
}

/**
 * Big hero summary card — total spent / total limit / % used. Used at
 * the top of the Budgets page. Tier color is the same `budgetLevel`
 * semantic as the per-row bar.
 */
export function BudgetHeroSummary({ totalSpent, totalLimit, currency, formatMoney, t }) {
  const pct = totalLimit > 0 ? totalSpent / totalLimit : 0;
  const level = budgetLevel(pct);
  const remaining = totalLimit - totalSpent;
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4 mb-3">
      <div className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
        {t('budgets.heroTotal')}
      </div>
      <div className="flex items-baseline gap-2 mb-2">
        <span className={`text-2xl font-semibold tabular-nums ${level === 'over' ? 'text-red-700 dark:text-red-300' : 'text-slate-900 dark:text-slate-100'}`}>
          {formatMoney(totalSpent, currency)}
        </span>
        <span className="text-sm text-slate-400 dark:text-slate-500">
          / {formatMoney(totalLimit, currency)}
        </span>
      </div>
      <BudgetProgressBar
        pct={pct}
        ariaLabel={t('budgets.heroPctAria', { pct: formatPercent(pct, 0) })}
      />
      <div className="flex items-center justify-between mt-2">
        <span className={`text-xs font-medium tabular-nums ${budgetTextColor(level)}`}>
          {formatPercent(pct, 0)}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400 tabular-nums">
          {level === 'over'
            ? t('budgets.heroOverBy', { amount: formatMoney(Math.abs(remaining), currency) })
            : t('budgets.heroRemaining', { amount: formatMoney(remaining, currency) })}
        </span>
      </div>
    </div>
  );
}
